import { createApi } from '@reduxjs/toolkit/query/react';
import { Endpoint } from 'services/endpoints';

import HTTPMethod, { Response } from 'types/http';
import { UserRecord } from 'types/user';

import { getDefaultPagination } from 'utils/pagination';

import { AccountsListQuery, AccountsListResponse, AvailableAccountsQuery, AvailableAccountsResponse } from '../accounts/types';
import { fetchBaseQueryWithAuth } from '../api';
import { TAGS } from './keys';
import {
  ChangePasswordRequestBody,
  ChangePasswordResponse,
  ProfileInfoResponse,
  ProfileUpdateRequestBody,
  ProfileUpdateResponse,
  RevokeAccessToAccountRequestBody,
  RevokeAccessToAccountResponse,
} from './types';

const REDUCER_PATH = 'profileApi';

export const profileApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQueryWithAuth,
  tagTypes: [TAGS.PROFILE_ACCOUNT_LIST, TAGS.PROFILE_AVAILABLE_ACCOUNTS_LIST],
  endpoints: (builder) => ({
    getAccountsList: builder.query<AccountsListResponse, AccountsListQuery>({
      query: (requestBody) => ({
        url: Endpoint.Accounts.List,
        method: HTTPMethod.POST,
        body: requestBody,
      }),
      providesTags: [TAGS.PROFILE_ACCOUNT_LIST],
      transformResponse: (response: Response<AccountsListResponse>) => {
        if (!response?.data) {
          return { data: [], pagination: getDefaultPagination(), requestId: response.requestId };
        }

        return response.data;
      },
    }),
    getAvailableAccounts: builder.query<AvailableAccountsResponse, AvailableAccountsQuery>({
      query: (requestBody) => ({
        url: Endpoint.Accounts.Available,
        method: HTTPMethod.POST,
        body: requestBody,
      }),
      transformResponse: (response: Response<AvailableAccountsResponse>) => {
        if (!response?.data) {
          return { data: [], pagination: getDefaultPagination(), requestId: response.requestId };
        }

        return response.data;
      },
      providesTags: [TAGS.PROFILE_AVAILABLE_ACCOUNTS_LIST],
    }),
    getDirectAccessAccounts: builder.query<AvailableAccountsResponse, AvailableAccountsQuery>({
      query: (requestBody) => ({
        url: Endpoint.Accounts.DirectAccess,
        method: HTTPMethod.POST,
        body: requestBody,
      }),
      transformResponse: (response: Response<AvailableAccountsResponse>) => {
        if (!response?.data) {
          return { data: [], pagination: getDefaultPagination(), requestId: response.requestId };
        }

        return response.data;
      },
      providesTags: [TAGS.PROFILE_AVAILABLE_ACCOUNTS_LIST],
    }),
    getProfile: builder.query<UserRecord, void>({
      query: () => ({
        url: Endpoint.Profile.Me,
        method: HTTPMethod.GET,
      }),
      transformResponse: (response: ProfileInfoResponse) => {
        return response.data!;
      },
    }),
    updateProfile: builder.mutation<ProfileUpdateResponse, ProfileUpdateRequestBody>({
      query: (requestBody) => ({
        url: Endpoint.Profile.Me,
        method: HTTPMethod.PUT,
        body: requestBody,
      }),
      invalidatesTags: (result, error) => (error ? [] : [TAGS.PROFILE_ACCOUNT_LIST, TAGS.PROFILE_AVAILABLE_ACCOUNTS_LIST]),
    }),
    revokeAccessToAccount: builder.mutation<RevokeAccessToAccountResponse, RevokeAccessToAccountRequestBody>({
      query: ({ accountUuid, userUuid }) => ({
        url: Endpoint.Users.RevokeAccess(accountUuid, userUuid),
        method: HTTPMethod.DELETE,
        body: {},
      }),
      invalidatesTags: (result, error) => (error ? [] : [TAGS.PROFILE_ACCOUNT_LIST, TAGS.PROFILE_AVAILABLE_ACCOUNTS_LIST]),
    }),
    changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequestBody>({
      query: (requestBody) => ({
        url: Endpoint.Users.ChangePassword,
        method: HTTPMethod.POST,
        body: requestBody,
      }),
    }),
  }),
});

export const {
  useGetProfileQuery,
  useGetDirectAccessAccountsQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useGetAccountsListQuery,
  useGetAvailableAccountsQuery,
  useRevokeAccessToAccountMutation,
} = profileApi;
export default profileApi;
